import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import debounce from 'debounce';
import { EDLShadeRenderer } from './edlShadeRenderer';
import { POINT_ATTENUATION_SCALE, PointCloudMaterial } from '../PointCloudMaterial';
import { CALLBACK_ORDER_EDL_SHADES } from '../FrameCallbackOrder';

type Props = {
  gl: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  enableEDL: boolean;
  edlStrength: number;
  invalidateFrame: () => void;
};

export const useEdlRenderer = ({
  gl,
  scene,
  camera,
  enableEDL,
  edlStrength,
  invalidateFrame,
}: Props) => {
  const screenSizeReference = useRef(new THREE.Vector2());
  const getCanvasSize = useCallback(() => {
    gl.getSize(screenSizeReference.current);
    return {
      width: Math.round(screenSizeReference.current.x),
      height: Math.round(screenSizeReference.current.y),
    };
  }, [gl]);

  const [canvasSize, setCanvasSize] = useState(getCanvasSize());

  const handleResize = useCallback(() => {
    const { width, height } = getCanvasSize();
    setCanvasSize((current) => {
      if (width === 0 || (current.width === width && current.height === height)) {
        return current;
      }
      return { width, height };
    });
  }, [getCanvasSize]);

  const debouncedResize = debounce(handleResize, 300);

  useEffect(() => {
    if (!gl.domElement) return;
    const resizeObserver = new ResizeObserver(debouncedResize);
    resizeObserver.observe(gl.domElement);
    return () => resizeObserver.disconnect(); // clean up
  }, [gl, debouncedResize]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: need canvasSize
  const edlRenderer = useMemo(() => {
    return new EDLShadeRenderer(gl, scene, camera);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl, scene, camera, canvasSize]);

  useEffect(() => {
    return () => {
      edlRenderer.dispose();
    };
  }, [edlRenderer]);

  useEffect(() => {
    if (edlRenderer) {
      edlRenderer.setEdlStrength(edlStrength);
      invalidateFrame();
    }
  }, [edlRenderer, edlStrength, invalidateFrame]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: invalidates frame when enableEDL changed
  useEffect(() => {
    invalidateFrame();
  }, [enableEDL, invalidateFrame]);

  return edlRenderer;
};

type UseEdlEffectProps = {
  gl: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.Camera;
  enableEDL: boolean;
  edlRenderer: EDLShadeRenderer | undefined;
  pointCloudMaterialMap: Map<string, PointCloudMaterial>;
};

export const useEdlEffect = ({
  gl,
  enableEDL,
  edlRenderer,
  pointCloudMaterialMap,
}: UseEdlEffectProps) => {
  const canvasSize = useRef<THREE.Vector2>(new THREE.Vector2(-1, -1));

  const updateMaterialsScale = useCallback(() => {
    const previousHeight = canvasSize.current.y;
    gl.getSize(canvasSize.current);
    const height = canvasSize.current.y;
    if (height !== previousHeight) {
      const scale = height * POINT_ATTENUATION_SCALE;
      pointCloudMaterialMap.forEach(({ shaderMaterial }: PointCloudMaterial) => {
        shaderMaterial.uniforms.scale.value = scale;
        shaderMaterial.needsUpdate = true;
      });
    }
  }, [gl, pointCloudMaterialMap]);

  useFrame((_state, timeDelta) => {
    updateMaterialsScale();
    if (edlRenderer) {
      pointCloudMaterialMap.forEach(({ shaderMaterial }: PointCloudMaterial) => {
        shaderMaterial.uniforms.enableEDL.value = 0;
        shaderMaterial.needsUpdate = true;
      });

      if (enableEDL) {
        edlRenderer.render(timeDelta, pointCloudMaterialMap);

        pointCloudMaterialMap.forEach(({ shaderMaterial }) => {
          shaderMaterial.uniforms.enableEDL.value = 1;
          shaderMaterial.uniforms.edlShade0.value = edlRenderer.targets[0].texture;
          shaderMaterial.uniforms.edlShade1.value = edlRenderer.targets[1].texture;
          shaderMaterial.uniforms.edlShade2.value = edlRenderer.targets[2].texture;
          gl.getDrawingBufferSize(
            shaderMaterial.uniforms.screenSize.value as unknown as THREE.Vector2
          );
          shaderMaterial.needsUpdate = true;
        });
      }

      gl.autoClear = true;
    }
  }, CALLBACK_ORDER_EDL_SHADES);
};
